import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Form,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function UsersClockEntriesPage() {
  const [users, setUsers] = useState([]);
  const [clockEntries, setClockEntries] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDocRef = doc(db, "Users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userRole = userDoc.data().role || "";
            setCurrentUserRole(userRole);

            if (userRole === "admin") {
              setRolePermissions({ canViewClockInOut: true });
            } else {
              const roleDocRef = doc(db, "Roles", userRole);
              const roleDoc = await getDoc(roleDocRef);

              if (roleDoc.exists()) {
                setRolePermissions(roleDoc.data().permissions);
              } else {
                setRolePermissions(null);
              }
            }
          }
        }
      });
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (rolePermissions && !rolePermissions.canViewClockInOut && currentUserRole !== "admin") {
      setAccessDenied(true);
    }
  }, [rolePermissions]);

  useEffect(() => {
    const fetchUsersAndEntries = async () => {
      setLoading(true);
      try {
        const usersSnapshot = await getDocs(collection(db, "Users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);

        const clockEntriesSnapshot = await getDocs(collection(db, "clock_entries"));
        const entriesData = clockEntriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClockEntries(entriesData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsersAndEntries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (text) => {
    setSearchText(text);
    setDropdownVisible(true);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchText(`${user.fullName} (${user.email})`);
    setDropdownVisible(false);
    calculateWeeklyHours(user, selectedMonth);
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    if (selectedUser) {
      calculateWeeklyHours(selectedUser, month);
    }
  };

  const calculateWeeklyHours = (user, month) => {
    if (!month || !user) return;

    const startOfMonth = new Date(`${month}-01`);
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0);

    const userEntries = clockEntries.filter(
      (entry) => entry.email === user.email
    );

    const weeks = Array(5).fill(0); // For maximum 5 weeks in a month
    const monthlyEntries = [];

    userEntries.forEach((entry) => {
      const clockIn = new Date(entry.clockIn);
      const clockOut = entry.clockOut ? new Date(entry.clockOut) : null;

      if (clockIn >= startOfMonth && clockIn <= endOfMonth && clockOut) {
        const weekIndex = Math.floor((clockIn.getDate() - 1) / 7); // Determine the week number
        const duration = (clockOut - clockIn) / 1000 / 60 / 60; // Duration in hours

        // Calculate total break duration
        const totalBreaks = (entry.breaks || []).reduce(
          (sum, breakItem) => sum + (breakItem.duration || 0),
          0
        );

        weeks[weekIndex] += duration;

        // Add entry to monthly entries
        monthlyEntries.push({
          clockIn: clockIn.toLocaleString(),
          clockOut: clockOut.toLocaleString(),
          duration: duration.toFixed(2),
          breaks: totalBreaks.toFixed(2), // Total break duration
        });
      }
    });

    setWeeklyHours(weeks.map((hours) => hours.toFixed(2))); // Round to 2 decimals
    setFilteredEntries(monthlyEntries); // Update filtered entries for the table
  };

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">You do not have permission to access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4 p-xxl-5">
        <h1 className="text-center mb-4">Weekly Hours and Clocks by User</h1>

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Search and Month Selection */}
            <Row className="mb-4">
              <Col lg={6} ref={dropdownRef}>
                <Form.Label>Search by User</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Type to search"
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => setDropdownVisible(true)}
                  />
                </InputGroup>
                {dropdownVisible && (
                  <div className="dropdown-menu w-100 show">
                    {users
                      .filter((user) =>
                        `${user.fullName} (${user.email})`
                          .toLowerCase()
                          .includes(searchText.toLowerCase())
                      )
                      .map((user) => (
                        <div
                          key={user.id}
                          className="dropdown-item"
                          onClick={() => handleUserSelect(user)}
                        >
                          {user.fullName} ({user.email})
                        </div>
                      ))}
                  </div>
                )}
              </Col>
              <Col lg={6}>
                <Form.Label>Select Month</Form.Label>
                <Form.Control
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                />
              </Col>
            </Row>

            {/* Weekly Hours Section */}
            {selectedUser && selectedMonth && (
              <Card className="shadow-sm rounded-3 mb-4">
                <Card.Header className="bg-primary text-white">
                  Weekly Hours for {selectedUser.fullName}
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyHours.map((hours, index) => (
                        <tr key={index}>
                          <td>Week {index + 1}</td>
                          <td>{hours} hours</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* All Clocks Section */}
            {filteredEntries.length > 0 && (
              <Card className="shadow-sm rounded-3">
                <Card.Header className="bg-secondary text-white">
                  All Clocks for {selectedUser.fullName} in {selectedMonth}
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Clock In</th>
                        <th>Clock Out</th>
                        <th>Duration (hours)</th>
                        <th>Breaks (minutes)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEntries.map((entry, index) => (
                        <tr key={index}>
                          <td>{entry.clockIn}</td>
                          <td>{entry.clockOut}</td>
                          <td>{entry.duration}</td>
                          <td>{entry.breaks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
      <Footer />
    </React.Fragment>
  );
}
